import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import AuthPage from './pages/AuthPage';
import HomePage from './pages/HomePage';
import ItemsPage from './pages/ItemsPage';
import SessionCheckPage from './pages/SessionCheckPage';
import { clearStoredToken, getCurrentUser, logoutUser } from './services/authApi';
import PaymentTypePage from './pages/PaymentTypePage';
import PosPage from './pages/PosPage';
import ScannerPage from './pages/ScannerPage';
import SalesPage from './pages/SalesPage';

const USER_KEY = 'pos_user';

function App() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [user, setUser] = useState(() => {
    const rawUser = localStorage.getItem(USER_KEY);
    return rawUser ? JSON.parse(rawUser) : null;
  });
  const isAuthenticated = Boolean(user);

  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        localStorage.setItem(USER_KEY, JSON.stringify(currentUser));
      } catch (requestError) {
        setUser(null);
        clearStoredToken();
        localStorage.removeItem(USER_KEY);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    loadCurrentUser();
  }, []);

  const handleAuthenticated = (nextUser) => {
    setUser(nextUser);
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser || null));
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (requestError) {
      // no-op: local state clear is enough
    } finally {
      localStorage.removeItem(USER_KEY);
      setUser(null);
    }
  };

  if (isCheckingAuth) {
    return <SessionCheckPage />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={<AuthPage onAuthenticated={handleAuthenticated} isAuthenticated={isAuthenticated} />}
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <HomePage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <PosPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/items"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ItemsPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/types"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <PaymentTypePage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <SalesPage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route path="/scanner" element={<ScannerPage />} />
        <Route path="*" element={<Navigate to={isAuthenticated ? '/home' : '/'} replace />} />
      </Routes>
      
    </BrowserRouter>
  );
}

export default App;
